import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CornerDownRight } from "lucide-react";
import { PROTOCOLS } from "@/data/content";
import { Reveal, EASE } from "@/components/motion";

const last = PROTOCOLS.length - 1;

const Node = ({ p, i, active, setActive }) => {
  const reached = i <= active;
  return (
    <button
      onMouseEnter={() => setActive(i)}
      onClick={() => setActive(i)}
      data-testid={`protocol-node-${i + 1}`}
      className="group relative flex flex-col items-center text-center"
    >
      <span className={`relative grid place-items-center w-16 h-16 rounded-full border font-data text-sm transition-[background-color,border-color,color] duration-500 ${
        i === active ? "bg-[#C5A059] border-[#C5A059] text-[#070A12]" : reached ? "bg-[#0B1120] border-[#C5A059] text-[#E2C68C]" : "bg-[#0B1120] border-white/20 text-slate-400 group-hover:border-[#C5A059]/60"
      }`}>
        {i === active && <motion.span layoutId="protocol-halo" className="absolute -inset-3 rounded-full border border-[#C5A059]/40" transition={{ duration: 0.6, ease: EASE }} />}
        {p.n}
      </span>
      <span className={`mt-6 font-display text-2xl xl:text-3xl transition-colors duration-500 ${i === active ? "text-slate-50" : "text-slate-400 group-hover:text-slate-200"}`}>{p.name}</span>
    </button>
  );
};

const DesktopPath = ({ active, setActive }) => {
  const p = PROTOCOLS[active];
  const next = PROTOCOLS[active + 1];
  return (
    <div className="hidden lg:block" data-testid="protocol-path-desktop">
      <div className="relative">
        <div className="absolute top-8 left-[10%] right-[10%] h-px bg-white/10" />
        <motion.div
          className="absolute top-8 left-[10%] h-px bg-gradient-to-r from-[#8C713D] to-[#E2C68C] origin-left"
          initial={{ width: "0%" }}
          animate={{ width: `${(active / last) * 80}%` }}
          transition={{ duration: 0.9, ease: EASE }}
        />
        <div className="relative grid grid-cols-5">
          {PROTOCOLS.map((s, i) => <Node key={s.n} p={s} i={i} active={active} setActive={setActive} />)}
        </div>
      </div>

      <div className="mt-16 grid grid-cols-12 gap-10 items-start border-t border-white/10 pt-12 min-h-[220px]">
        <div className="col-span-7 grid">
          <AnimatePresence initial={false}>
            <motion.div
              key={active}
              className="[grid-area:1/1]"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE, delay: 0.1 } }}
              exit={{ opacity: 0, y: -12, transition: { duration: 0.3 } }}
              data-testid="protocol-detail"
            >
              <p className="font-data text-[11px] tracking-[0.25em] uppercase text-slate-400">Step {p.n} of 0{PROTOCOLS.length}</p>
              <h3 className="font-display text-5xl text-slate-50 mt-3">{p.name}</h3>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-xl">{p.text}</p>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="col-span-5 flex flex-col items-end gap-6">
          <div className="text-right" data-testid="protocol-leads-to">
            <p className="eyebrow mb-2">{next ? "Leads to" : "The outcome"}</p>
            <p className="font-display text-3xl text-[#E2C68C]">{next ? `${next.n} · ${next.name}` : "Post-Traumatic Growth"}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setActive(Math.max(0, active - 1))} disabled={active === 0} data-testid="protocol-prev-button" aria-label="Previous step"
              className="w-12 h-12 grid place-items-center border border-white/20 text-slate-100 hover:border-[#C5A059] disabled:opacity-30 transition-colors duration-300">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setActive(Math.min(last, active + 1))} disabled={active === last} data-testid="protocol-next-button" aria-label="Next step"
              className="w-12 h-12 grid place-items-center border border-white/20 text-slate-100 hover:border-[#C5A059] disabled:opacity-30 transition-colors duration-300">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MobilePath = () => (
  <ol className="lg:hidden relative" data-testid="protocol-path-mobile">
    <span className="absolute left-6 top-6 bottom-6 w-px bg-white/10" />
    <motion.span
      className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-[#8C713D] to-[#E2C68C] origin-top"
      initial={{ scaleY: 0 }}
      whileInView={{ scaleY: 1 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 2, ease: EASE }}
    />
    {PROTOCOLS.map((p, i) => {
      const next = PROTOCOLS[i + 1];
      return (
        <li key={p.n} className="relative pl-20 pb-12 last:pb-0" data-testid={`protocol-item-${i + 1}`}>
          <span className="absolute left-0 top-0 grid place-items-center w-12 h-12 rounded-full border border-[#C5A059] bg-[#0B1120] font-data text-xs text-[#E2C68C]">{p.n}</span>
          <h3 className="font-display text-3xl text-slate-50 pt-1">{p.name}</h3>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">{p.text}</p>
          <p className="mt-4 flex items-center gap-2 font-data text-[11px] tracking-[0.2em] uppercase text-[#C5A059]">
            <CornerDownRight className="w-3.5 h-3.5" /> {next ? `Leads to ${next.name}` : "Outcome: Post-Traumatic Growth"}
          </p>
        </li>
      );
    })}
  </ol>
);

export default function ProtocolPath() {
  const [active, setActive] = useState(0);
  return (
    <Reveal className="mt-20 sm:mt-28 border-t border-[#C5A059]/20 pt-8" data-testid="protocol-path">
      <div className="flex items-baseline justify-between gap-6 mb-14">
        <p className="eyebrow">The proprietary 5-protocol framework</p>
        <p className="hidden lg:block text-xs text-slate-400">Hover or select a step to follow the path</p>
      </div>
      <DesktopPath active={active} setActive={setActive} />
      <MobilePath />
    </Reveal>
  );
}
